import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { filmsApi } from "../api/filmsApi";
import { scheduleInvalidation } from "../api/deferredInvalidation";
import { Vtransaction_Entity } from "../../entities/vtransaction";

const vtransactionsAdapter = createEntityAdapter<Vtransaction_Entity>();
const initialState = vtransactionsAdapter.getInitialState();

export const vtransactionEndpoints = filmsApi.injectEndpoints({
  endpoints: (build) => ({
    getVtransactions: build.query<
      EntityState<Vtransaction_Entity>,
      Record<string, unknown> | void
    >({
      query: (args) => {
        return {
          url: `vtransactions`,
          params: args,
        };
      },
      transformResponse: (responseData: Vtransaction_Entity[]) => {
        return vtransactionsAdapter.setAll(initialState, responseData);
      },
      providesTags: ["vtransactions"],
    }),
    getVtransaction: build.query<Vtransaction_Entity, number>({
      query: (id) => `vtransactions/${id}`,
      providesTags: (result, error, id) => [{ type: "vtransactions", id }],
    }),
    createVtransaction: build.mutation<
      Vtransaction_Entity,
      Partial<Vtransaction_Entity>
    >({
      query: (body) => ({
        method: "POST",
        body,
        url: `vtransactions`,
      }),
      invalidatesTags: ["vtransactions"],
    }),
    updateVtransaction: build.mutation<
      void,
      { id: number; body: Partial<Vtransaction_Entity> }
    >({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `vtransactions/${args.id}`,
      }),
      // Deliberately no invalidatesTags — refreshing per cell rebuilt the grid
      // mid-typing and dropped input. See scheduleInvalidation.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // The grid reverts the cell and reports it; still refresh so the row
          // settles on what the server actually holds.
        }
        scheduleInvalidation(dispatch, ["vtransactions"]);
      },
    }),
    deleteVtransaction: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `vtransactions/${id}`,
      }),
      invalidatesTags: ["vtransactions"],
    }),
  }),
});

export const {
  useGetVtransactionQuery,
  useGetVtransactionsQuery,
  useCreateVtransactionMutation,
  useDeleteVtransactionMutation,
  useUpdateVtransactionMutation,
} = vtransactionEndpoints;

export default vtransactionEndpoints;
